
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import {
  TransformAttributes,
  TransformAttribute,
  TransformAttributeType,
  TransformAttributeCombine,
  Condition,
  SENSES,
  SKILLS,
  Tool,
  UpgradableNumber,
} from "@dndModels";
import { TRUE_DAMAGE_TYPES } from "@dndModels/Constants";

import DandDAPI from "@utilities/smart_api";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import LoadingBox from "@components/input/LoadingBox";
import FlexBox from '@components/layout/FlexBox';
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

interface Props {
  attributes: TransformAttributes;
  disabled: boolean;
  use_creature: boolean;
  onChange: () => void;
}

const api = DandDAPI.getInstance();

const CATEGORIES: { attribute: TransformAttributeType, name: string }[] = [
  { attribute: "speeds", name: "Speeds" },
  { attribute: "senses", name: "Senses" },
  { attribute: "skill_profs", name: "Skills" },
  { attribute: "save_profs", name: "Saving Throws" },
  { attribute: "tool_profs", name: "Tools" },
  { attribute: "damage_immunities", name: "Damage Immunities" },
  { attribute: "damage_resistances", name: "Damage Resistances" },
  { attribute: "damage_vulnerabilities", name: "Damage Vulnerabilities" },
  { attribute: "condition_immunities", name: "Condition Immunities" },
  { attribute: "languages", name: "Languages" },
];

const COMBINE_HELP: { [key in TransformAttributeCombine]: string } = {
  "Highest": "Whichever is highest of the inherited one and the Value.",
  "Add": "The inherited one plus the Value. Ex. +10 ft Speed, or Darkvision 60 ft (+60 ft if you already have it).",
  "Replace": "Just the Value. Anything inherited is ignored.",
};

/**
 * Each attribute (ex. Walking Speed, Darkvision, Stealth) is picked individually,
 * and then decides whether it inherits from the Target and/or the Creature,
 * and how that's combined with what the Transform specifies.
 */
export default function TransformAttributesInput({
  attributes,
  disabled,
  use_creature,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [category, setCategory] = useState<TransformAttributeType | "">("");
  const [edit_key, setEditKey] = useState<string | null>(null);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["condition","tool"] });
    if (res) {
      setConditions(res.condition as Condition[]);
      setTools(res.tool as Tool[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  const changed = () => {
    attributes.cleanUp();
    onChange();
    setPing(!ping);
  }

  const getKeys = (attribute: TransformAttributeType): { key: string, name: string }[] => {
    let keys: { key: string, name: string }[] = [];
    if (attribute === "speeds") {
      keys = [
        { key: "walk", name: "Walk" },
        { key: "swim", name: "Swim" },
        { key: "climb", name: "Climb" },
        { key: "fly", name: "Fly" },
        { key: "burrow", name: "Burrow" },
        { key: "hover", name: "Hover" },
      ];
    } else if (attribute === "senses") {
      keys = SENSES.map(o => ({ key: o.name, name: o.name }));
    } else if (attribute === "skill_profs") {
      keys = [...SKILLS].sort((a,b) => a.name.localeCompare(b.name)).map(o => ({ key: o._id, name: o.name }));
    } else if (attribute === "save_profs") {
      keys = ["STR","DEX","CON","INT","WIS","CHA"].map(o => ({ key: o, name: o }));
    } else if (attribute === "tool_profs") {
      keys = tools.map(o => ({ key: o.name, name: o.name }));
    } else if (attribute === "condition_immunities") {
      keys = conditions.filter(o => o.common).map(o => ({ key: o.name, name: o.name }));
    } else if (attribute === "languages") {
      keys = [{ key: "", name: "Languages" }];
    } else {
      keys = [
        ...TRUE_DAMAGE_TYPES.map(o => ({ key: o as string, name: o as string })),
        { key: "extra", name: "Extra (Text)" }
      ];
    }
    // Include any that are set but aren't in the list (ex. a Tool that's since been renamed).
    for (const entry of attributes.entries.filter(o => o.attribute === attribute)) {
      if (!keys.some(o => o.key === entry.key)) {
        keys.push({ key: entry.key, name: entry.key });
      }
    }
    return keys;
  }

  const describe = (entry: TransformAttribute | undefined) => {
    if (!entry) {
      return "";
    }
    const inherited: string[] = [];
    if (entry.from_target) {
      inherited.push("Target");
    }
    if (entry.from_creature && use_creature) {
      inherited.push("Creature");
    }
    const from = inherited.join(" or ");
    if (entry.kind === "flag") {
      return entry.grant ? "Yes" : `If ${from} has it`;
    } else if (entry.kind === "text") {
      const parts = entry.combine === "Replace" ? [] : inherited;
      return [...parts, entry.text].filter(o => o !== "").join(" + ");
    }
    const value = entry.value.isBlank() ? "" : (entry.value.isBasic() ? `${entry.value.base}` : "Formula");
    if (entry.combine === "Replace" || from === "") {
      return value === "" ? "None" : value;
    } else if (value === "") {
      return from;
    } else if (entry.combine === "Add") {
      return `${from} + ${value}`;
    }
    return `Highest of ${from}, ${value}`;
  }

  const describeCategory = (attribute: TransformAttributeType) => {
    const keys = getKeys(attribute);
    const entries = attributes.entries.filter(o => o.attribute === attribute);
    if (attribute === "languages") {
      return entries.map(o => describe(o)).join(", ");
    }
    // Group the ones with the same setting together, so ex. "All (Target)" instead of listing every Skill.
    const groups: { description: string, keys: string[] }[] = [];
    for (const k of keys) {
      const entry = entries.find(o => o.key === k.key);
      if (entry) {
        const description = describe(entry);
        const group = groups.find(o => o.description === description);
        if (group) {
          group.keys.push(k.key);
        } else {
          groups.push({ description, keys: [k.key] });
        }
      }
    }
    const nameOf = (key: string) => keys.find(k => k.key === key)?.name ?? key;
    return groups.map(group => {
      let names = group.keys.map(nameOf).join(", ");
      if (group.keys.length === keys.length) {
        names = "All";
      } else if (group.keys.length > 1 && group.keys.length > keys.length / 2) {
        names = `All except ${keys.filter(k => !group.keys.includes(k.key)).map(k => k.name).join(", ")}`;
      }
      return `${names} (${group.description})`;
    }).join("; ");
  }

  const renderBack = (header: string, onBack: () => void) => {
    return (
      <>
        <Grid size={2} style={{ cursor: "pointer", fontWeight: "bold" }}
          onClick={onBack}>
          Back
        </Grid>
        <Grid size={10} className="header">
          { header }
        </Grid>
      </>
    );
  }

  const renderCombine = (entry: TransformAttribute | undefined, attribute: TransformAttributeType, key: string, options: TransformAttributeCombine[]) => {
    const combine = entry ? entry.combine : "Highest";
    // Highest and Add work the same for text.
    const value = options.includes(combine) ? combine : options[0];
    return (
      <>
        <Grid size={12}>
          <SelectStringBox
            name="Combine With Inherited"
            options={options}
            value={value}
            disabled={disabled}
            onChange={(changed_value: string) => {
              attributes.getOrAddEntry(attribute, key).combine = changed_value as TransformAttributeCombine;
              changed();
            }}
          />
        </Grid>
        <Grid size={12}>{ COMBINE_HELP[value] }</Grid>
      </>
    );
  }

  const renderEditor = (attribute: TransformAttributeType, key: string) => {
    const entry = attributes.getEntry(attribute, key);
    const kind = TransformAttribute.kindOf(attribute, key);
    const name = getKeys(attribute).find(o => o.key === key)?.name ?? key;
    const category_name = CATEGORIES.find(o => o.attribute === attribute)?.name ?? "";
    return (
      <Grid container spacing={1} direction="row">
        { renderBack(attribute === "languages" ? name : `${category_name}: ${name}`, () => {
          setEditKey(null);
          if (attribute === "languages") {
            setCategory("");
          }
        })}
        <Grid size={6}>
          <CheckBox
            name="Inherit from Target"
            value={ entry ? entry.from_target : false }
            disabled={disabled}
            onChange={(changed_value) => {
              attributes.getOrAddEntry(attribute, key).from_target = changed_value;
              changed();
            }}
          />
        </Grid>
        <Grid size={6}>
          { use_creature &&
            <CheckBox
              name="Inherit from Creature"
              value={ entry ? entry.from_creature : false }
              disabled={disabled}
              onChange={(changed_value) => {
                attributes.getOrAddEntry(attribute, key).from_creature = changed_value;
                changed();
              }}
            />
          }
        </Grid>
        { kind === "number" &&
          <>
            { renderCombine(entry, attribute, key, ["Highest","Add","Replace"]) }
            <Grid size={12}>
              <UpgradableNumberBox
                name={ attribute === "skill_profs" ? "Value (1 = Proficient, 2 = Expertise)" : "Value" }
                value={ entry ? entry.value : new UpgradableNumber() }
                disabled={disabled}
                onChange={(changed_value: UpgradableNumber) => {
                  attributes.getOrAddEntry(attribute, key).value = changed_value.clone();
                  changed();
                }}
              />
            </Grid>
            <Grid size={12}>
              Ability Scores in the Value use the transformed ones.
              { attribute === "speeds" ? " Walking Speed is calculated first, so the other Speeds can use the transformed Walking Speed." : " Speeds in the Value use the transformed ones." }
            </Grid>
          </>
        }
        { kind === "flag" &&
          <Grid size={12}>
            <CheckBox
              name="Granted by Transform"
              value={ entry ? entry.grant : false }
              disabled={disabled}
              onChange={(changed_value) => {
                attributes.getOrAddEntry(attribute, key).grant = changed_value;
                changed();
              }}
            />
          </Grid>
        }
        { kind === "text" &&
          <>
            { renderCombine(entry, attribute, key, ["Add","Replace"]) }
            <Grid size={12}>
              <StringBox
                name="Text"
                value={ entry ? entry.text : "" }
                disabled={disabled}
                onBlur={(changed_value: string) => {
                  attributes.getOrAddEntry(attribute, key).text = changed_value;
                  changed();
                }}
              />
            </Grid>
          </>
        }
        <Grid size={12}>
          <ToggleButtonBox
            name="Clear"
            value={false}
            disabled={disabled || !entry}
            onToggle={() => {
              attributes.entries = attributes.entries.filter(o => o !== entry);
              changed();
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderInheritAll = (attribute: TransformAttributeType, keys: { key: string }[], side: "from_target" | "from_creature", name: string) => {
    const all = keys.every(k => attributes.getEntry(attribute, k.key)?.[side]);
    return (
      <Grid size={6}>
        <ToggleButtonBox
          name={name}
          value={all}
          disabled={disabled}
          onToggle={() => {
            for (const k of keys) {
              attributes.getOrAddEntry(attribute, k.key)[side] = !all;
            }
            changed();
          }}
        />
      </Grid>
    );
  }

  const renderCategory = (attribute: TransformAttributeType) => {
    const keys = getKeys(attribute);
    const category_name = CATEGORIES.find(o => o.attribute === attribute)?.name ?? "";
    return (
      <Grid container spacing={1} direction="row">
        { renderBack(category_name, () => { setCategory(""); }) }
        { renderInheritAll(attribute, keys, "from_target", "Inherit All from Target") }
        { use_creature && renderInheritAll(attribute, keys, "from_creature", "Inherit All from Creature") }
        { keys.map(k => {
          const description = describe(attributes.getEntry(attribute, k.key));
          return (
            <Grid key={k.key} size={4} style={{ cursor: "pointer" }}
              onClick={() => { setEditKey(k.key); }}>
              <b>{ k.name }</b> { description === "" ? "--" : description }
            </Grid>
          );
        })}
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else if (category !== "" && edit_key !== null) {
    return renderEditor(category, edit_key);
  } else if (category !== "") {
    return renderCategory(category);
  }
  return (
    <FlexBox direction="column" style={{ cursor: "pointer" }}>
      <FlexBox xs_expand={0} className="header centered">Transform Attributes</FlexBox>
      { CATEGORIES.map(o => (
        <FlexBox key={o.attribute} onClick={() => {
          setCategory(o.attribute);
          setEditKey(o.attribute === "languages" ? "" : null);
        }}>
          <b>{ o.name }</b>&nbsp;{ describeCategory(o.attribute) }
        </FlexBox>
      ))}
    </FlexBox>
  );
}
